import {DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy, arrayMove} from '@dnd-kit/sortable';
import {Dropdown, openDeleteDialog, openDialog, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useCallback, useMemo, useRef, useState} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {VIRTUAL_PRESET_ID} from '../../constants/VIRTUAL_PRESET_ID';
import {useVirtualPresetPinned} from '../../store/selectors/useVirtualPresetPinned';
import {useEagGridStore} from '../../store/useEagGridStore';
import type {Preset} from '../../types/Api';
import {isPresetCustom} from '../../utils/isPresetCustom';
import {ChevronDownGlyph, PlusGlyph} from '../icons/StrokeGlyphs';
import {EditPresetDialog} from './components/EditPresetDialog/EditPresetDialog';
import {PinnedPresetTab} from './components/PinnedPresetTab/PinnedPresetTab';
import {PresetActionsMenu} from './components/PresetActionsMenu/PresetActionsMenu';
import {PresetRow} from './components/PresetRow/PresetRow';
import {PRESET_DROPDOWN_STYLES} from './constants/PRESET_DROPDOWN_STYLES';
import {PRESET_SCROLLBAR_CLASSES} from './constants/PRESET_SCROLLBAR_CLASSES';
import {PresetApiStateEnum} from './constants/PresetApiStateEnum';
import {usePresetApiState} from './hooks/usePresetApiState';
import {usePresetColorPreview} from './hooks/usePresetColorPreview';
import {usePresetRowCounts} from './hooks/usePresetRowCounts';
import type {PresetsPanelRendererProps} from './types/PresetsPanelRendererProps';
import {
  DRAG_ACTIVATION_DISTANCE,
  PRESET_LIST_MIN_HEIGHT_PX,
  makeVirtualRowCountEntry,
  setPresetListMaxHeight,
} from './utils/presetPanelConstants';
import {resolvePresetColor} from './utils/resolvePresetColor';

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function PresetDropdownRenderer(props: PresetsPanelRendererProps) {
  const t = useTranslationContext();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const {resolveColor, setPreviewColor} = usePresetColorPreview(props.presets);
  const store = useEagGridStore();
  const isVirtualPinned = useVirtualPresetPinned();
  const searchRef = useRef<HTMLInputElement>(null);

  const {
    apiState,
    copyState,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    applyPresetToVirtualProxy,
    isPresetChangeDisabled,
    updatePresetPositionProxy,
    activatePresetProxy,
  } = usePresetApiState({
    updatePreset: props.updatePreset,
    duplicatePreset: props.duplicatePreset,
    deletePreset: props.deletePreset,
    applyPresetToVirtual: props.applyPresetToVirtual,
    updatePresetPosition: props.updatePresetPosition,
  });

  const activePresetId = isPresetCustom(props.activePreset) ? props.activePreset.id : undefined;
  const isVirtualActive = isNotNil(props.activePreset) && !isPresetCustom(props.activePreset);

  const rowCountItems = useMemo(() => {
    if (!props.virtualPreset || isVirtualActive) {
      return props.presets;
    }
    return [makeVirtualRowCountEntry(props.virtualPreset), ...(props.presets ?? [])];
  }, [props.presets, props.virtualPreset, isVirtualActive]);

  const rowCounts = usePresetRowCounts({
    visibleItems: rowCountItems,
    rowCountGetter: props.rowCountGetter,
  });
  const presetsRowCount = rowCounts.presetsRowCount;

  const activePreset = props.presets?.find((p) => p.id === activePresetId);
  const activeLabel = isVirtualActive
    ? t('page.datagrid.labels.defaultView')
    : (activePreset?.title ?? t('page.datagrid.labels.defaultView'));

  const currentActiveId = isVirtualActive ? VIRTUAL_PRESET_ID : activePresetId;

  // Local order state for drag-and-drop — synced from props.presets
  const [localPresets, setLocalPresets] = useState<Preset[]>(props.presets ?? []);
  const [prevPresets, setPrevPresets] = useState(props.presets);

  if (prevPresets !== props.presets) {
    setPrevPresets(props.presets);
    setLocalPresets(props.presets ?? []);
  }

  // DnD sensors — require 8px drag distance to distinguish from clicks
  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isPresetChangeDisabled) {
      return;
    }
    const {active, over} = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = localPresets.findIndex((p) => p.id === active.id);
    const newIndex = localPresets.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    // Optimistic local reorder
    setLocalPresets((prev) => arrayMove(prev, oldIndex, newIndex));
    // Persist via API
    updatePresetPositionProxy(active.id as string, oldIndex, newIndex);
  };

  // Filter presets by search query
  const defaultLabel = t('page.datagrid.labels.defaultView');
  const isSearchActive = search.trim().length > 0;
  const filteredPresets = useMemo(() => {
    if (!isSearchActive) {
      return localPresets;
    }
    const q = search.toLowerCase();
    return localPresets.filter((p) => p.title.toLowerCase().includes(q));
  }, [localPresets, search, isSearchActive]);

  const showVirtual =
    props.virtualPreset &&
    (!isSearchActive || defaultLabel.toLowerCase().includes(search.toLowerCase()));

  const pinnedPresets = localPresets.filter((preset) => preset.isPinned);
  const activeColor = currentActiveId
    ? resolveColor(currentActiveId, activePreset?.color)
    : undefined;

  // Keep pinning optimistic while persisting the preference on the backend.
  const togglePin = (preset: Preset) => {
    const nextPinned = !preset.isPinned;
    setLocalPresets((prev) =>
      prev.map((item) => (item.id === preset.id ? {...item, isPinned: nextPinned} : item))
    );
    void props.updatePresetPin(preset.id, nextPinned).catch(() => {
      setLocalPresets((prev) =>
        prev.map((item) => (item.id === preset.id ? {...item, isPinned: preset.isPinned} : item))
      );
    });
  };

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 0);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const handleSelectPreset = (presetId: string) => {
    if (presetId === currentActiveId || isPresetChangeDisabled) {
      return;
    }
    if (presetId === VIRTUAL_PRESET_ID) {
      activatePresetProxy(VIRTUAL_PRESET_ID, () => props.deactivatePreset());
    } else {
      const preset = props.presets?.find((p) => p.id === presetId);
      if (preset) {
        activatePresetProxy(preset.id, () => props.activatePreset(preset));
      }
    }
    handleClose();
  };

  const openCreateDialog = () => {
    openDialog(
      <EditPresetDialog
        onSubmit={(title, description, color) => props.createPreset(title, description, color)}
        data-testid={suffixTestId('createPreset', props)}
      />,
      {
        id: 'createPreset',
        title: t('general.presets.createPreset'),
        'data-testid': suffixTestId('createPreset', props),
        size: 'small',
        scrollBehavior: 'outside',
      }
    );
  };

  return (
    // Keep the panel within the remaining toolbar width and scroll overflowing presets.
    <div
      className={`flex flex-nowrap items-center gap-2 flex-1 min-w-0 overflow-x-auto [&>*]:shrink-0 ${PRESET_SCROLLBAR_CLASSES}`}
    >
      {/* Dropdown trigger + panel */}
      <Dropdown
        isLazy
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        isKeyboardNavigationDisabled
        isFocusManagementDisabled
        data-testid={suffixTestId('presetDropdown', props)}
        dropdownControl={
          <button
            type="button"
            data-testid={suffixTestId('presetDropdownTrigger', props)}
            className="inline-flex items-center gap-2 h-9 px-3! rounded-md cursor-pointer select-none text-inherit focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <span
              className="size-3 shrink-0 rounded-full"
              style={{backgroundColor: resolvePresetColor(activeColor)}}
            />
            <span className="truncate max-w-70 text-sm font-medium">{activeLabel}</span>
            <ChevronDownGlyph size={14} />
          </button>
        }
      >
        <div
          className={`w-68 max-w-[calc(100vw-var(--spacing-8))] ${PRESET_DROPDOWN_STYLES.colors}`}
        >
          {/* Search */}
          <div className="relative pb-1">
            <span className="pointer-events-none absolute left-2 top-2 text-[color:var(--preset-menu-muted)] opacity-50">
              <SearchIcon />
            </span>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t('general.labels.search')}
              placeholder={t('general.labels.search') + '...'}
              onKeyDown={(e) => e.stopPropagation()}
              className={PRESET_DROPDOWN_STYLES.search}
              data-testid={suffixTestId('presetDropdown-search', props)}
            />
          </div>

          {/* Preset list — fills the space below the trigger down to a fixed gap
              from the bottom viewport edge. Sized by the setPresetListMaxHeight
              callback ref from the list's MEASURED position (the header stack above
              the trigger varies, so a static calc(100vh - N) reserve either wasted
              space or ran off-screen). The inline maxHeight is the pre-measure floor. */}
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div
              ref={setPresetListMaxHeight}
              style={{
                maxHeight: PRESET_LIST_MIN_HEIGHT_PX,
                overflowY: 'auto',
              }}
            >
              {/* Virtual "Default" preset — not sortable */}
              <Show when={showVirtual ? props.virtualPreset : null}>
                {(virtualPreset) => (
                  <PresetRow
                    id={VIRTUAL_PRESET_ID}
                    label={defaultLabel}
                    color={resolveColor(VIRTUAL_PRESET_ID)}
                    selected={isVirtualActive}
                    pinned={isVirtualPinned}
                    isDraggable={false}
                    count={
                      isVirtualActive
                        ? props.activeViewRowCount
                        : presetsRowCount[virtualPreset.dataQueryId]
                    }
                    onClick={() => handleSelectPreset(VIRTUAL_PRESET_ID)}
                    onTogglePin={() => store.setVirtualPresetPinned(!isVirtualPinned)}
                    data-testid={suffixTestId('preset-dropdown-default', props)}
                  />
                )}
              </Show>

              {/* Saved presets — sortable via drag-and-drop */}
              <SortableContext
                items={filteredPresets.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredPresets.map((preset) => {
                  const isActive = preset.id === activePresetId;
                  return (
                    <PresetRow
                      key={preset.id}
                      id={preset.id}
                      label={preset.title}
                      color={resolveColor(preset.id, preset.color)}
                      selected={isActive}
                      pinned={preset.isPinned ?? false}
                      isDraggable={!isSearchActive}
                      count={
                        isActive ? props.activeViewRowCount : presetsRowCount[preset.dataQueryId]
                      }
                      onClick={() => handleSelectPreset(preset.id)}
                      // Same actions menu as tab mode's kebab (update/reset/edit/
                      // duplicate/share/delete, incl. their gating). Delete keeps the
                      // dropdown-mode confirm dialog.
                      copyIsLoading={copyState[preset.id] === PresetApiStateEnum.loading}
                      actions={(closeMenu) => (
                        <PresetActionsMenu
                          preset={preset}
                          presetChanged={props.isPresetChanged}
                          activePresetId={activePresetId}
                          presetColor={resolveColor(preset.id, preset.color)}
                          onColorChange={setPreviewColor}
                          updatePreset={updatePresetProxy}
                          deletePreset={(presetId) =>
                            openDeleteDialog({
                              name: preset.title,
                              onConfirm: () => deletePresetProxy(presetId),
                              'data-testid': suffixTestId('deletePreset', props),
                            })
                          }
                          duplicatePreset={duplicatePresetProxy}
                          applyPresetToVirtual={applyPresetToVirtualProxy}
                          applyToDefaultState={copyState[preset.id]}
                          onCopySuccess={closeMenu}
                          applyToDefaultDisabled={isPresetChangeDisabled}
                          resetOverrides={props.resetOverrides}
                          renamePreset={props.renamePreset}
                          testId={suffixTestId(`preset-dropdown-${preset.title}`, props)}
                          editDialogTestId={suffixTestId('editPreset', props)}
                          dialogOptionsTestId={suffixTestId('editPreset', props)}
                          dialogId="editPreset"
                        />
                      )}
                      onTogglePin={() => togglePin(preset)}
                      data-testid={suffixTestId(`preset-dropdown-${preset.title}`, props)}
                    />
                  );
                })}
              </SortableContext>

              <Show when={filteredPresets.length === 0 && !showVirtual}>
                <div
                  style={{
                    padding: '16px 8px',
                    textAlign: 'center',
                    fontSize: 13,
                    color: '#97A0AF',
                  }}
                >
                  {t('general.labels.noResultsFound')}
                </div>
              </Show>
            </div>
          </DndContext>
        </div>
      </Dropdown>

      {/* Create preset button */}
      <button
        type="button"
        disabled={isPresetChangeDisabled}
        onClick={openCreateDialog}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          border: 'none',
          background: 'transparent',
          borderRadius: 4,
          cursor: 'pointer',
          color: '#42526E',
        }}
        className="hover:bg-[#F4F5F7]"
        data-testid={suffixTestId('preset-dropdown-create', props)}
      >
        <PlusGlyph size={16} />
      </button>

      {/* Pinned preset tabs */}
      <Show when={pinnedPresets.length > 0 || (isVirtualPinned && isNotNil(props.virtualPreset))}>
        <>
          {/* Divider */}
          <div
            role="separator"
            aria-orientation="vertical"
            style={{
              width: 1,
              height: 20,
              background: '#DFE1E6',
              flexShrink: 0,
            }}
          />

          <Show when={isVirtualPinned ? props.virtualPreset : null}>
            {(virtualPreset) => (
              <PinnedPresetTab
                label={defaultLabel}
                color={resolveColor(VIRTUAL_PRESET_ID)}
                count={
                  isVirtualActive
                    ? props.activeViewRowCount
                    : presetsRowCount[virtualPreset.dataQueryId]
                }
                active={isVirtualActive}
                isLoading={
                  apiState[VIRTUAL_PRESET_ID] === PresetApiStateEnum.loading ||
                  (isVirtualActive && props.activeViewIsLoading)
                }
                onClick={() => handleSelectPreset(VIRTUAL_PRESET_ID)}
                onTogglePin={() => store.setVirtualPresetPinned(false)}
                data-testid={suffixTestId('preset-pinned-default', props)}
              />
            )}
          </Show>

          {pinnedPresets.map((preset) => {
            const isActive = preset.id === activePresetId;
            const count = isActive ? props.activeViewRowCount : presetsRowCount[preset.dataQueryId];
            const state = apiState[preset.id] ?? PresetApiStateEnum.idle;

            return (
              <PinnedPresetTab
                key={preset.id}
                label={preset.title}
                color={resolveColor(preset.id, preset.color)}
                count={count}
                active={isActive}
                unsaved={props.isPresetChanged && isActive}
                isLoading={
                  state === PresetApiStateEnum.loading || (isActive && props.activeViewIsLoading)
                }
                onClick={() => {
                  if (!isActive && !isPresetChangeDisabled) {
                    activatePresetProxy(preset.id, () => props.activatePreset(preset));
                  }
                }}
                onTogglePin={() => togglePin(preset)}
                copyIsLoading={copyState[preset.id] === PresetApiStateEnum.loading}
                actions={(closeMenu) => (
                  <PresetActionsMenu
                    preset={preset}
                    presetChanged={props.isPresetChanged}
                    activePresetId={activePresetId}
                    presetColor={resolveColor(preset.id, preset.color)}
                    onColorChange={setPreviewColor}
                    updatePreset={updatePresetProxy}
                    deletePreset={(presetId) =>
                      openDeleteDialog({
                        name: preset.title,
                        onConfirm: () => deletePresetProxy(presetId),
                        'data-testid': suffixTestId('deletePreset', props),
                      })
                    }
                    duplicatePreset={duplicatePresetProxy}
                    applyPresetToVirtual={applyPresetToVirtualProxy}
                    applyToDefaultState={copyState[preset.id]}
                    onCopySuccess={closeMenu}
                    applyToDefaultDisabled={isPresetChangeDisabled}
                    resetOverrides={props.resetOverrides}
                    renamePreset={props.renamePreset}
                    testId={suffixTestId(`preset-pinned-${preset.title}`, props)}
                    editDialogTestId={suffixTestId('editPreset', props)}
                    dialogOptionsTestId={suffixTestId('editPreset', props)}
                    dialogId="editPreset"
                  />
                )}
                data-testid={suffixTestId(`preset-pinned-${preset.title}`, props)}
              />
            );
          })}
        </>
      </Show>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx={10} cy={10} r={6} />
      <path d="m15 15 5 5" />
    </svg>
  );
}
